import { useState, useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Upload, FileText, CheckCircle, XCircle, Loader2, ExternalLink, RefreshCw, Camera, Lock, AlertTriangle, ArrowLeft, FileStack, DollarSign, Users, Building2 } from "lucide-react";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [sheetsConnected, setSheetsConnected] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [uploaderName, setUploaderName] = useState("");
  const [purpose, setPurpose] = useState("");
  const [purposeDetails, setPurposeDetails] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [value, setValue] = useState("");
  const [currency, setCurrency] = useState("");
  const [paymentType, setPaymentType] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [userComments, setUserComments] = useState(""); // Optional comments field
  const [isAdmin, setIsAdmin] = useState(false);
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const batchFileInputRef = useRef(null);
  
  // Multi-page invoice analysis state
  const [analyzing, setAnalyzing] = useState(false);
  const [multiPageResult, setMultiPageResult] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showMultiPageReview, setShowMultiPageReview] = useState(false);
  const [confirmedCurrency, setConfirmedCurrency] = useState("");

  // Batch upload state
  const [uploadMode, setUploadMode] = useState("single"); // "single" or "batch"
  const [batchFiles, setBatchFiles] = useState([]);
  const [batchUploading, setBatchUploading] = useState(false);
  const [batchResults, setBatchResults] = useState(null);
  const [showBatchResults, setShowBatchResults] = useState(false);
  const [batchMismatch, setBatchMismatch] = useState(null); // {userTotal, extractedTotal, difference}
  const [correctedBatchTotal, setCorrectedBatchTotal] = useState("");
  const [showUploadSuccess, setShowUploadSuccess] = useState(false); // Track successful upload

  // Employee management state
  const [employees, setEmployees] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [showEmployeeManager, setShowEmployeeManager] = useState(false);
  const [newEmployeeName, setNewEmployeeName] = useState("");
  const [newEmployeeCompany, setNewEmployeeCompany] = useState("");
  const [editingEmployee, setEditingEmployee] = useState(null);

  // Purpose management state
  const [purposes, setPurposes] = useState([]);
  const [allPurposes, setAllPurposes] = useState([]);
  const [showPurposeManager, setShowPurposeManager] = useState(false);
  const [newPurposeName, setNewPurposeName] = useState("");
  const [editingPurpose, setEditingPurpose] = useState(null);

  // Company management state
  const [companies, setCompanies] = useState([]);
  const [allCompanies, setAllCompanies] = useState([]);
  const [showCompanyManager, setShowCompanyManager] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState("");
  const [editingCompany, setEditingCompany] = useState(null);

  // Clarification re-submission state
  const [clarifyInvoiceId, setClarifyInvoiceId] = useState(null);
  const [clarificationComments, setClarificationComments] = useState("");
  const [clarificationResponse, setClarificationResponse] = useState("");
  const [isResubmitting, setIsResubmitting] = useState(false);

  // List of admin emails who can view all invoices - read from environment variable
  const ADMIN_EMAILS = (process.env.REACT_APP_ADMIN_EMAILS || "").split(",").map(e => e.trim().toLowerCase()).filter(e => e);

  // Company options (kept for backwards compatibility, but employees come from DB)
  // COMPANY_OPTIONS will be fetched from API via 'companies' state

  const CURRENCY_OPTIONS = [
    // Major currencies
    { value: "GBP", label: "£ GBP - British Pound", symbol: "£" },
    { value: "USD", label: "$ USD - US Dollar", symbol: "$" },
    { value: "EUR", label: "€ EUR - Euro", symbol: "€" },
    // Asia Pacific
    { value: "JPY", label: "¥ JPY - Japanese Yen", symbol: "¥" },
    { value: "CNY", label: "¥ CNY - Chinese Yuan", symbol: "¥" },
    { value: "INR", label: "₹ INR - Indian Rupee", symbol: "₹" },
    { value: "KRW", label: "₩ KRW - South Korean Won", symbol: "₩" },
    { value: "SGD", label: "$ SGD - Singapore Dollar", symbol: "S$" },
    { value: "HKD", label: "$ HKD - Hong Kong Dollar", symbol: "HK$" },
    { value: "AUD", label: "$ AUD - Australian Dollar", symbol: "A$" },
    { value: "NZD", label: "$ NZD - New Zealand Dollar", symbol: "NZ$" },
    { value: "THB", label: "฿ THB - Thai Baht", symbol: "฿" },
    { value: "MYR", label: "RM MYR - Malaysian Ringgit", symbol: "RM" },
    { value: "IDR", label: "Rp IDR - Indonesian Rupiah", symbol: "Rp" },
    { value: "PHP", label: "₱ PHP - Philippine Peso", symbol: "₱" },
    { value: "VND", label: "₫ VND - Vietnamese Dong", symbol: "₫" },
    // Middle East
    { value: "AED", label: "د.إ AED - UAE Dirham", symbol: "د.إ" },
    { value: "SAR", label: "﷼ SAR - Saudi Riyal", symbol: "﷼" },
    { value: "ILS", label: "₪ ILS - Israeli Shekel", symbol: "₪" },
    { value: "TRY", label: "₺ TRY - Turkish Lira", symbol: "₺" },
    { value: "QAR", label: "﷼ QAR - Qatari Riyal", symbol: "﷼" },
    // Europe
    { value: "CHF", label: "CHF - Swiss Franc", symbol: "CHF" },
    { value: "SEK", label: "kr SEK - Swedish Krona", symbol: "kr" },
    { value: "NOK", label: "kr NOK - Norwegian Krone", symbol: "kr" },
    { value: "DKK", label: "kr DKK - Danish Krone", symbol: "kr" },
    { value: "PLN", label: "zł PLN - Polish Zloty", symbol: "zł" },
    { value: "CZK", label: "Kč CZK - Czech Koruna", symbol: "Kč" },
    { value: "HUF", label: "Ft HUF - Hungarian Forint", symbol: "Ft" },
    { value: "RON", label: "lei RON - Romanian Leu", symbol: "lei" },
    // Americas
    { value: "CAD", label: "$ CAD - Canadian Dollar", symbol: "C$" },
    { value: "MXN", label: "$ MXN - Mexican Peso", symbol: "MX$" },
    { value: "BRL", label: "R$ BRL - Brazilian Real", symbol: "R$" },
    { value: "ARS", label: "$ ARS - Argentine Peso", symbol: "AR$" },
    { value: "CLP", label: "$ CLP - Chilean Peso", symbol: "CL$" },
    { value: "COP", label: "$ COP - Colombian Peso", symbol: "CO$" },
    // Africa
    { value: "ZAR", label: "R ZAR - South African Rand", symbol: "R" },
    { value: "EGP", label: "£ EGP - Egyptian Pound", symbol: "E£" },
    { value: "NGN", label: "₦ NGN - Nigerian Naira", symbol: "₦" },
    { value: "KES", label: "KSh KES - Kenyan Shilling", symbol: "KSh" },
    // Other
    { value: "RUB", label: "₽ RUB - Russian Ruble", symbol: "₽" },
  ];

  const PAYMENT_TYPE_OPTIONS = [
    "Reimbursement",
    "Paid from Company Card"
  ];

  // PURPOSE_OPTIONS will be fetched from API via 'purposes' state

  // Editable Amount Component for batch invoice breakdown
  const EditableAmount = ({ amount, currency, onSave }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState("");
    
    const currencySymbols = { GBP: "£", USD: "$", EUR: "€", INR: "₹", AED: "د.إ" };
    const symbol = currencySymbols[currency] || currency + " ";
    
    // Parse the numeric value from the amount string
    const numericValue = parseFloat((amount || "0").replace(/[^0-9.-]/g, '')) || 0;
    
    const startEditing = () => {
      setEditValue(numericValue.toFixed(2));
      setIsEditing(true);
    };
    
    const handleSave = () => {
      const newValue = parseFloat(editValue.replace(',', '.')) || 0;
      if (newValue !== numericValue) {
        onSave(newValue.toFixed(2));
      }
      setIsEditing(false);
    };
    
    const handleKeyDown = (e) => {
      if (e.key === 'Enter') {
        handleSave();
      } else if (e.key === 'Escape') {
        setIsEditing(false);
      }
    };
    
    if (isEditing) {
      return (
        <div className="flex items-center gap-1">
          <span className="text-sm">{symbol}</span>
          <input
            type="number"
            step="0.01"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleSave}
            onKeyDown={handleKeyDown}
            autoFocus
            className="w-24 px-2 py-1 text-right border border-blue-500 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      );
    }
    
    return (
      <button
        onClick={startEditing}
        className="font-medium hover:bg-blue-50 hover:text-blue-600 px-2 py-1 rounded transition-colors cursor-pointer border border-transparent hover:border-blue-200"
        title="Click to edit amount"
      >
        {symbol}{numericValue.toFixed(2)}
      </button>
    );
  };

  // Get employees list filtered by selected company
  const getEmployeesForCompany = () => {
    if (!companyName) return [];
    return employees.filter(emp => emp.company === companyName).map(emp => emp.name);
  };

  // Reset employee when company changes
  const handleCompanyChange = (newCompany) => {
    setCompanyName(newCompany);
    setUploaderName(""); // Reset employee selection when company changes
  };

  // Helper function to check if purpose requires description
  const requiresDescription = (purposeName) => {
    if (!purposeName) return false;
    const lower = purposeName.toLowerCase();
    return lower.includes("misc") || lower.includes("other");
  };

  // Handle purpose change
  const handlePurposeChange = (newPurpose) => {
    setPurpose(newPurpose);
    if (!requiresDescription(newPurpose)) {
      setPurposeDetails(""); // Clear details if description not required
    }
  };

  useEffect(() => {
    checkSheetsConnection();
    
    // Load saved uploader name from localStorage
    const savedName = localStorage.getItem("uploaderName");
    if (savedName) {
      setUploaderName(savedName);
    }

    // Check if user is admin
    const savedAdminEmail = localStorage.getItem("adminEmail");
    if (savedAdminEmail && ADMIN_EMAILS.includes(savedAdminEmail.toLowerCase())) {
      setAdminEmail(savedAdminEmail);
      setIsAdmin(true);
      fetchInvoices(); // Only fetch if admin
    }
    
    // Fetch employees from API
    fetchEmployees();
    
    // Fetch purposes from API
    fetchPurposes();
    
    // Fetch companies from API
    fetchCompanies();
    
    // Check if coming from clarification link
    const urlParams = new URLSearchParams(window.location.search);
    const clarifyId = urlParams.get('clarify');
    if (clarifyId) {
      fetchClarificationData(clarifyId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch invoice data for clarification re-submission
  const fetchClarificationData = async (invoiceId) => {
    try {
      const response = await axios.get(`${API}/invoices/${invoiceId}/prefill`);
      const data = response.data;
      
      // Pre-fill all form fields
      setClarifyInvoiceId(invoiceId);
      setUploaderName(data.uploader_name || "");
      setUserEmail(data.user_email || "");
      setCompanyName(data.company_name || "");
      setPurpose(data.purpose || "");
      setValue(data.user_value || "");
      setCurrency(data.user_currency || "");
      setPaymentType(data.payment_type || "Reimbursement");
      setUserComments(data.user_comments || "");
      setClarificationComments(data.clarification_comments || "");
      
      // Show notification
      toast.info("Your previous submission has been loaded. Please make the requested changes and re-submit.", {
        duration: 6000
      });
      
      // Clear the URL parameter without refreshing
      window.history.replaceState({}, document.title, window.location.pathname);
      
    } catch (error) {
      console.error("Error fetching clarification data:", error);
      toast.error("Could not load previous submission data. Please fill in the form manually.");
    }
  };

  // Handle resubmitting after clarification (without re-uploading file)
  const handleResubmitClarification = async () => {
    if (!clarifyInvoiceId || !clarificationResponse.trim()) {
      toast.error("Please enter your response to the clarification request.");
      return;
    }
    
    // Validate comments - mandatory
    if (!userComments || !userComments.trim()) {
      toast.error("Additional Comments is required. Please provide comments before resubmitting.");
      return;
    }
    
    setIsResubmitting(true);
    
    try {
      const response = await axios.post(`${API}/invoices/${clarifyInvoiceId}/resubmit-clarification`, {
        clarification_response: clarificationResponse,
        user_value: value,
        user_comments: userComments,
        purpose: purpose
      });
      
      if (response.data.success) {
        toast.success("Invoice resubmitted successfully! The admin will be notified.");
        // Clear clarification state
        setClarifyInvoiceId(null);
        setClarificationComments("");
        setClarificationResponse("");
        // Clear form
        setValue("");
        setUserComments("");
        setShowUploadSuccess(true);
        setTimeout(() => setShowUploadSuccess(false), 10000);
      } else {
        toast.error(response.data.message || "Failed to resubmit. Please try again.");
      }
    } catch (error) {
      console.error("Error resubmitting clarification:", error);
      toast.error(error.response?.data?.detail || "Failed to resubmit. Please try again.");
    } finally {
      setIsResubmitting(false);
    }
  };

  // Track loading failures for showing retry button
  const [loadingFailed, setLoadingFailed] = useState(false);

  // Retry all failed data loads
  const retryAllLoads = async () => {
    setLoadingFailed(false);
    toast.info("Retrying...");
    await Promise.all([
      fetchEmployees(0),
      fetchPurposes(0),
      fetchCompanies(0)
    ]);
  };

  // Fetch employees from API with improved retry (exponential backoff)
  const fetchEmployees = async (retryCount = 0) => {
    try {
      const response = await axios.get(`${API}/employees`, { timeout: 15000 });
      setEmployees(response.data);
      return true;
    } catch (error) {
      console.error("Error fetching employees:", error);
      if (retryCount < 3) {
        const delay = Math.min(1000 * Math.pow(2, retryCount), 5000); // 1s, 2s, 4s (max 5s)
        await new Promise(r => setTimeout(r, delay));
        return fetchEmployees(retryCount + 1);
      } else {
        toast.error("Failed to load employees. Click 'Retry' or refresh the page.");
        setEmployees([]);
        setLoadingFailed(true);
        return false;
      }
    }
  };

  // Fetch all employees including inactive (for admin management)
  const fetchAllEmployees = async () => {
    try {
      const response = await axios.get(`${API}/employees/all`);
      setAllEmployees(response.data);
    } catch (error) {
      console.error("Error fetching all employees:", error);
    }
  };

  // Add new employee
  const addEmployee = async () => {
    if (!newEmployeeName.trim()) {
      toast.error("Please enter employee name");
      return;
    }
    if (!newEmployeeCompany) {
      toast.error("Please select a company");
      return;
    }
    try {
      await axios.post(`${API}/employees`, { 
        name: newEmployeeName.trim(),
        company: newEmployeeCompany
      });
      toast.success("Employee added successfully");
      setNewEmployeeName("");
      setNewEmployeeCompany("");
      fetchEmployees();
      fetchAllEmployees();
    } catch (error) {
      console.error("Error adding employee:", error);
      toast.error(error.response?.data?.detail || "Failed to add employee");
    }
  };

  // Update employee
  const updateEmployee = async (id, data) => {
    try {
      await axios.put(`${API}/employees/${id}`, data);
      toast.success("Employee updated successfully");
      setEditingEmployee(null);
      fetchEmployees();
      fetchAllEmployees();
    } catch (error) {
      console.error("Error updating employee:", error);
      toast.error("Failed to update employee");
    }
  };

  // Delete (deactivate) employee
  const deleteEmployee = async (id) => {
    if (!window.confirm("Are you sure you want to deactivate this employee?")) return;
    try {
      await axios.delete(`${API}/employees/${id}`);
      toast.success("Employee deactivated");
      fetchEmployees();
      fetchAllEmployees();
    } catch (error) {
      console.error("Error deleting employee:", error);
      toast.error("Failed to deactivate employee");
    }
  };

  // Restore employee
  const restoreEmployee = async (id) => {
    try {
      await axios.post(`${API}/employees/${id}/restore`);
      toast.success("Employee restored");
      fetchEmployees();
      fetchAllEmployees();
    } catch (error) {
      console.error("Error restoring employee:", error);
      toast.error("Failed to restore employee");
    }
  };

  // ===== PURPOSE MANAGEMENT FUNCTIONS =====
  
  // Fetch active purposes (for dropdown) with retry
  const fetchPurposes = async (retryCount = 0) => {
    try {
      const response = await axios.get(`${API}/purposes`, { timeout: 15000 });
      setPurposes(response.data);
      if (response.data.length === 0 && retryCount === 0) {
        // Try seeding if empty
        try {
          await axios.get(`${API}/purposes/seed`);
          // Retry fetch after seeding
          const retryResponse = await axios.get(`${API}/purposes`);
          setPurposes(retryResponse.data);
        } catch (seedError) {
          console.error("Purpose seed failed:", seedError);
        }
      }
      return true;
    } catch (error) {
      console.error("Error fetching purposes:", error);
      if (retryCount < 3) {
        const delay = Math.min(1000 * Math.pow(2, retryCount), 5000);
        await new Promise(r => setTimeout(r, delay));
        return fetchPurposes(retryCount + 1);
      } else {
        toast.error("Failed to load purposes. Click 'Retry' or refresh the page.");
        setPurposes([]);
        setLoadingFailed(true);
        return false;
      }
    }
  };

  // Fetch all purposes including inactive (for admin management)
  const fetchAllPurposes = async () => {
    try {
      const response = await axios.get(`${API}/purposes/all`);
      setAllPurposes(response.data);
    } catch (error) {
      console.error("Error fetching all purposes:", error);
    }
  };

  // Add new purpose
  const addPurpose = async () => {
    if (!newPurposeName.trim()) {
      toast.error("Please enter purpose name");
      return;
    }
    try {
      await axios.post(`${API}/purposes`, { 
        name: newPurposeName.trim()
      });
      toast.success("Purpose added successfully");
      setNewPurposeName("");
      fetchPurposes();
      fetchAllPurposes();
    } catch (error) {
      console.error("Error adding purpose:", error);
      toast.error(error.response?.data?.detail || "Failed to add purpose");
    }
  };

  // Update purpose
  const updatePurpose = async (id, data) => {
    try {
      await axios.put(`${API}/purposes/${id}`, data);
      toast.success("Purpose updated successfully");
      setEditingPurpose(null);
      fetchPurposes();
      fetchAllPurposes();
    } catch (error) {
      console.error("Error updating purpose:", error);
      toast.error(error.response?.data?.detail || "Failed to update purpose");
    }
  };

  // Delete (deactivate) purpose
  const deletePurpose = async (id) => {
    if (!window.confirm("Are you sure you want to deactivate this purpose?")) return;
    try {
      await axios.delete(`${API}/purposes/${id}`);
      toast.success("Purpose deactivated");
      fetchPurposes();
      fetchAllPurposes();
    } catch (error) {
      console.error("Error deleting purpose:", error);
      toast.error("Failed to deactivate purpose");
    }
  };

  // Restore purpose
  const restorePurpose = async (id) => {
    try {
      await axios.post(`${API}/purposes/${id}/restore`);
      toast.success("Purpose restored");
      fetchPurposes();
      fetchAllPurposes();
    } catch (error) {
      console.error("Error restoring purpose:", error);
      toast.error("Failed to restore purpose");
    }
  };

  // ===== COMPANY MANAGEMENT FUNCTIONS =====
  
  // Fetch active companies (for dropdown) with improved retry
  const fetchCompanies = async (retryCount = 0) => {
    try {
      const response = await axios.get(`${API}/companies`, { timeout: 15000 });
      setCompanies(response.data);
      if (response.data.length === 0 && retryCount === 0) {
        // Try seeding if empty
        try {
          await axios.get(`${API}/companies/seed`);
          // Retry fetch after seeding
          const retryResponse = await axios.get(`${API}/companies`);
          setCompanies(retryResponse.data);
        } catch (seedError) {
          console.error("Company seed failed:", seedError);
        }
      }
      return true;
    } catch (error) {
      console.error("Error fetching companies:", error);
      if (retryCount < 3) {
        const delay = Math.min(1000 * Math.pow(2, retryCount), 5000);
        await new Promise(r => setTimeout(r, delay));
        return fetchCompanies(retryCount + 1);
      } else {
        toast.error("Failed to load companies. Click 'Retry' or refresh the page.");
        setCompanies([]);
        setLoadingFailed(true);
        return false;
      }
    }
  };

  // Fetch all companies including inactive (for admin management)
  const fetchAllCompanies = async () => {
    try {
      const response = await axios.get(`${API}/companies/all`);
      setAllCompanies(response.data);
    } catch (error) {
      console.error("Error fetching all companies:", error);
    }
  };

  // Add new company
  const addCompany = async () => {
    if (!newCompanyName.trim()) {
      toast.error("Please enter company name");
      return;
    }
    try {
      await axios.post(`${API}/companies`, { 
        name: newCompanyName.trim()
      });
      toast.success("Company added successfully");
      setNewCompanyName("");
      fetchCompanies();
      fetchAllCompanies();
    } catch (error) {
      console.error("Error adding company:", error);
      toast.error(error.response?.data?.detail || "Failed to add company");
    }
  };

  // Update company
  const updateCompany = async (id, data) => {
    try {
      await axios.put(`${API}/companies/${id}`, data);
      toast.success("Company updated successfully");
      setEditingCompany(null);
      fetchCompanies();
      fetchAllCompanies();
    } catch (error) {
      console.error("Error updating company:", error);
      toast.error(error.response?.data?.detail || "Failed to update company");
    }
  };

  // Delete (deactivate) company
  const deleteCompany = async (id) => {
    if (!window.confirm("Are you sure you want to deactivate this company?")) return;
    try {
      await axios.delete(`${API}/companies/${id}`);
      toast.success("Company deactivated");
      fetchCompanies();
      fetchAllCompanies();
    } catch (error) {
      console.error("Error deleting company:", error);
      toast.error("Failed to deactivate company");
    }
  };

  // Restore company
  const restoreCompany = async (id) => {
    try {
      await axios.post(`${API}/companies/${id}/restore`);
      toast.success("Company restored");
      fetchCompanies();
      fetchAllCompanies();
    } catch (error) {
      console.error("Error restoring company:", error);
      toast.error("Failed to restore company");
    }
  };

  // Effect to show modal only when multiPageResult is actually set
  useEffect(() => {
    if (multiPageResult !== null && !analyzing) {
      console.log("multiPageResult updated:", multiPageResult);
    }
  }, [multiPageResult, analyzing]);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/invoices`);
      setInvoices(response.data);
    } catch (error) {
      console.error("Error fetching invoices:", error);
      toast.error("Failed to load invoices");
    } finally {
      setLoading(false);
    }
  };

  const checkSheetsConnection = async () => {
    try {
      const response = await axios.get(`${API}/sheets/status`);
      setSheetsConnected(response.data.connected);
    } catch (error) {
      console.error("Error checking sheets connection:", error);
    }
  };

  // Analyze multi-page invoice before upload
  const analyzeMultiPageInvoice = async (file) => {
    if (!file) return;
    
    // Clear previous values to avoid confusion
    setMultiPageResult(null);
    setSelectedFile(file);
    setAnalyzing(true);
    
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      toast.info(`Analyzing ${file.name}... This may take a minute for large files.`);
      
      const response = await axios.post(`${API}/invoices/analyze-multipage`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000, // 3 minute timeout for large files with many invoices
      });
      
      const result = response.data;
      console.log("Multi-page analysis result:", JSON.stringify(result, null, 2));
      
      if (!result) {
        throw new Error("Empty response from server");
      }
      
      // Set result first so modal can render it
      setMultiPageResult(result);
      
      // Auto-populate fields based on analysis
      if (result.invoices_detected > 0) {
        // Set total value from all detected invoices
        setValue(result.total_amount.toFixed(2));
        
        // Set currency if single currency found
        if (!result.has_currency_mismatch && result.suggested_currency) {
          setCurrency(result.suggested_currency);
          setConfirmedCurrency(result.suggested_currency);
        }
        
        // Store AI-extracted purposes as a hint for user, but don't auto-select a category
        // User must manually select an active purpose from the dropdown
        const purposes = (result.all_invoices || [])
          .filter(inv => inv.purpose)
          .map(inv => inv.purpose);
        if (purposes.length > 0) {
          // Don't auto-set to "Others/Misc" - it may be deactivated
          // Store the AI-detected description for reference but let user select purpose
          setPurposeDetails(purposes.join("; "));
          // Show a toast with AI-detected purpose info
          toast.info(`AI detected: ${purposes.join("; ").substring(0, 100)}...`);
        }
        
        if (result.has_currency_mismatch) {
          toast.warning(`Multiple currencies detected: ${result.currencies_found.join(", ")}. Please confirm the correct currency.`);
        } else {
          toast.success(`Found ${result.invoices_detected} invoice(s) totaling ${result.total_amount_formatted}`);
        }
      } else {
        // Result exists but no invoices detected
        toast.info("No invoices detected. You can still proceed with manual entry.");
      }
      
      // Show modal after state is set
      setAnalyzing(false);
      setShowMultiPageReview(true);
      
    } catch (error) {
      console.error("Error analyzing invoice:", error);
      let errorMsg = "Unknown error";
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorMsg = "Analysis timed out. The file may be too large. Try uploading a smaller file.";
      } else if (error.response?.data?.detail) {
        errorMsg = error.response.data.detail;
      } else if (error.message) {
        errorMsg = error.message;
      }
      toast.error(`Analysis failed: ${errorMsg}`);
      // On error, show modal with null result so user can proceed with manual entry
      setMultiPageResult(null);
      setAnalyzing(false);
      setShowMultiPageReview(true);
    }
  };

  // Handle file selection - analyze first for PDFs
  const handleFileSelect = async (file) => {
    if (!file) return;
    
    // For PDFs, always analyze first
    if (file.type === "application/pdf") {
      await analyzeMultiPageInvoice(file);
    } else {
      // For images, just set the file and show review
      setSelectedFile(file);
      setShowMultiPageReview(false);
      // Optionally analyze single images too
      await analyzeMultiPageInvoice(file);
    }
  };

  // Proceed with upload after review
  const proceedWithUpload = async () => {
    if (!selectedFile) return;
    
    // Use confirmed currency if there was a mismatch
    const finalCurrency = confirmedCurrency || currency;
    if (!finalCurrency) {
      toast.error("Please select a currency before proceeding");
      return;
    }
    setCurrency(finalCurrency);
    
    setShowMultiPageReview(false);
    await handleFileUpload(selectedFile);
  };

  // Cancel multi-page review
  const cancelMultiPageReview = () => {
    setShowMultiPageReview(false);
    setMultiPageResult(null);
    setSelectedFile(null);
    setConfirmedCurrency("");
  };

  const handleFileUpload = async (file) => {
    if (!file) return;

    if (!uploaderName.trim()) {
      toast.error("Please enter your name before uploading");
      return;
    }

    if (!companyName) {
      toast.error("Please select a company before uploading");
      return;
    }

    if (!value.trim()) {
      toast.error("Please enter the value before uploading");
      return;
    }

    if (!currency) {
      toast.error("Please select a currency before uploading");
      return;
    }

    if (!purpose.trim()) {
      toast.error("Please select a purpose before uploading");
      return;
    }

    if (requiresDescription(purpose) && !purposeDetails.trim()) {
      toast.error("Please provide a description for this purpose");
      return;
    }

    if (!paymentType) {
      toast.error("Please select a payment type before uploading");
      return;
    }

    if (!userComments.trim()) {
      toast.error("Please provide additional comments before uploading");
      return;
    }

    if (!userEmail.trim()) {
      toast.error("Please enter your email address before uploading");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(userEmail)) {
      toast.error("Please enter a valid email address");
      return;
    }

    // Combine purpose with details if description is required
    const finalPurpose = requiresDescription(purpose) && purposeDetails.trim() 
      ? `${purpose}: ${purposeDetails.trim()}`
      : purpose;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("uploader_name", uploaderName);
    formData.append("company_name", companyName);
    formData.append("user_value", value);
    formData.append("user_currency", currency);
    formData.append("purpose", finalPurpose);
    formData.append("payment_type", paymentType);
    formData.append("user_email", userEmail);
    if (userComments.trim()) {
      formData.append("user_comments", userComments.trim());
    }

    try {
      setUploading(true);
      toast.info(`Processing ${file.name}...`);
      
      const response = await axios.post(`${API}/invoices/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data.status === "completed") {
        toast.success("Invoice processed successfully!");
        setShowUploadSuccess(true); // Show success message
        // Auto-hide after 10 seconds
        setTimeout(() => setShowUploadSuccess(false), 10000);
      } else if (response.data.status === "failed") {
        toast.error(`Processing failed: ${response.data.error_message}`);
      }

      fetchInvoices();
      
      // Save uploader name to localStorage
      localStorage.setItem("uploaderName", uploaderName);
      
      // Clear fields after successful upload
      setPurpose("");
      setPurposeDetails("");
      setValue("");
      setCurrency("");
      setPaymentType("");
      setUserComments("");
      setMultiPageResult(null);
      setSelectedFile(null);
      setConfirmedCurrency("");
      // Clear clarification state
      setClarifyInvoiceId(null);
      setClarificationComments("");
      // Keep name and email for convenience
    } catch (error) {
      console.error("Error uploading invoice:", error);
      // Provide more helpful error message
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error("Upload timed out. Please check your connection and try again.");
      } else if (error.response?.status === 413) {
        toast.error("File too large. Please use a smaller file.");
      } else if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else if (!navigator.onLine) {
        toast.error("No internet connection. Please check your network.");
      } else {
        toast.error("Failed to upload invoice. Please try again.");
      }
    } finally {
      setUploading(false);
    }
  };

  // Batch upload functions
  const handleBatchFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 20) {
      toast.error("Maximum 20 files allowed per batch upload");
      return;
    }
    
    // Validate file types
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp", "application/pdf"];
    const invalidFiles = files.filter(f => !allowedTypes.includes(f.type));
    if (invalidFiles.length > 0) {
      toast.error(`Invalid file type(s): ${invalidFiles.map(f => f.name).join(", ")}`);
      return;
    }
    
    setBatchFiles(files);
  };

  const removeBatchFile = (index) => {
    setBatchFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleBatchUpload = async () => {
    if (batchFiles.length === 0) {
      toast.error("Please select files to upload");
      return;
    }

    if (!uploaderName.trim()) {
      toast.error("Please enter your name before uploading");
      return;
    }

    if (!companyName) {
      toast.error("Please select a company before uploading");
      return;
    }

    if (!currency) {
      toast.error("Please select a currency before uploading");
      return;
    }

    if (!purpose.trim()) {
      toast.error("Please select a purpose before uploading");
      return;
    }

    if (requiresDescription(purpose) && !purposeDetails.trim()) {
      toast.error("Please provide a description for this purpose");
      return;
    }

    if (!paymentType) {
      toast.error("Please select a payment type before uploading");
      return;
    }

    if (!userComments.trim()) {
      toast.error("Please provide additional comments before uploading");
      return;
    }

    if (!userEmail.trim()) {
      toast.error("Please enter your email address before uploading");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(userEmail)) {
      toast.error("Please enter a valid email address");
      return;
    }

    const finalPurpose = requiresDescription(purpose) && purposeDetails.trim() 
      ? `${purpose}: ${purposeDetails.trim()}`
      : purpose;

    const formData = new FormData();
    batchFiles.forEach(file => {
      formData.append("files", file);
    });
    formData.append("uploader_name", uploaderName);
    formData.append("company_name", companyName);
    formData.append("user_currency", currency);
    formData.append("purpose", finalPurpose);
    formData.append("payment_type", paymentType);
    formData.append("user_email", userEmail);
    formData.append("user_comments", userComments.trim());
    // Pass expected total if provided
    if (value.trim()) {
      formData.append("expected_total", value.trim());
    }

    try {
      setBatchUploading(true);
      toast.info(`Uploading ${batchFiles.length} files...`);
      
      const response = await axios.post(`${API}/invoices/batch-upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000, // 3 minute timeout for file upload (extraction is async)
      });

      const batchId = response.data.batch_id;
      
      // Check if extraction is still in progress (async mode)
      if (response.data.extraction_complete === false) {
        toast.info("Files uploaded! AI is extracting invoice data...");
        
        // Poll for extraction completion
        const pollInterval = 2500; // 2.5 seconds
        const maxPolls = 120; // Max 5 minutes of polling (120 * 2.5s = 300s)
        let pollCount = 0;
        
        const pollForCompletion = async () => {
          try {
            const statusResponse = await axios.get(`${API}/batch/${batchId}/status`, {
              params: { user_currency: currency },
              timeout: 10000
            });
            
            pollCount++;
            
            if (statusResponse.data.extraction_complete) {
              // Extraction complete - update results
              const finalData = {
                batch_id: batchId,
                total_files: statusResponse.data.total_invoices,
                processed_files: statusResponse.data.extraction_completed,
                failed_files: statusResponse.data.extraction_failed,
                invoices: statusResponse.data.invoices,
                total_amount: statusResponse.data.total_amount,
                message: `Processed ${statusResponse.data.extraction_completed} of ${statusResponse.data.total_invoices} files`,
                purpose: finalPurpose,
                extraction_complete: true
              };
              
              setBatchResults(finalData);
              handleBatchComplete(finalData);
              return;
            }
            
            // Update progress
            const pending = statusResponse.data.extraction_pending || 0;
            const completed = statusResponse.data.extraction_completed || 0;
            const total = statusResponse.data.total_invoices || 0;
            
            // Update partial results so user can see progress
            setBatchResults({
              batch_id: batchId,
              total_files: total,
              processed_files: completed,
              failed_files: statusResponse.data.extraction_failed || 0,
              invoices: statusResponse.data.invoices,
              total_amount: statusResponse.data.total_amount,
              message: `Extracting... ${completed}/${total} complete`,
              purpose: finalPurpose,
              extraction_complete: false
            });
            
            if (pollCount < maxPolls && pending > 0) {
              // Continue polling
              setTimeout(pollForCompletion, pollInterval);
            } else if (pollCount >= maxPolls) {
              toast.warning("Extraction taking longer than expected. Please refresh to check status.");
              setBatchUploading(false);
            }
          } catch (pollError) {
            console.error("Polling error:", pollError);
            if (pollCount < maxPolls) {
              // Retry on network errors
              setTimeout(pollForCompletion, pollInterval * 2); // Slower retry on error
            }
          }
        };
        
        setShowBatchResults(true);
        pollForCompletion();
        
      } else {
        // Synchronous extraction completed (legacy or single file)
        setBatchResults(response.data);
        handleBatchComplete(response.data);
      }
      
      fetchInvoices();
      localStorage.setItem("uploaderName", uploaderName);
      
      // Clear batch files but keep form data until after approval email is sent
      setBatchFiles([]);
      setPaymentType("");
      setUserComments("");
      setValue("");
      
    } catch (error) {
      console.error("Error uploading batch:", error);
      // Provide more helpful error message
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error("Upload timed out. Please try again or upload fewer files.");
      } else if (error.response?.status === 413) {
        toast.error("Files too large. Please reduce file sizes or upload fewer files.");
      } else if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else if (!navigator.onLine) {
        toast.error("No internet connection. Please check your network and try again.");
      } else {
        toast.error("Failed to upload batch. Please check your connection and try again.");
      }
      setBatchUploading(false);
    }
  };

  // Handle batch completion (after extraction finishes)
  const handleBatchComplete = (data) => {
    // Check for mismatch between user's expected total and extracted total
    const userExpectedTotal = parseFloat(value) || 0;
    const extractedTotalStr = data.total_amount || "0";
    const extractedTotal = parseFloat(extractedTotalStr.replace(/[^0-9.-]/g, '')) || 0;
    
    if (userExpectedTotal > 0 && Math.abs(userExpectedTotal - extractedTotal) > 0.01) {
      // There's a mismatch
      const difference = extractedTotal - userExpectedTotal;
      setBatchMismatch({
        userTotal: userExpectedTotal,
        extractedTotal: extractedTotal,
        difference: difference,
        currency: currency
      });
      setCorrectedBatchTotal(extractedTotal.toFixed(2));
      toast.warning("Total amount mismatch detected! Please review.");
    } else {
      setBatchMismatch(null);
      if (data.failed_files === 0) {
        toast.success(`All ${data.processed_files} invoices processed successfully!`);
      } else {
        toast.warning(`Processed ${data.processed_files} of ${data.total_files} files`);
      }
    }
    
    setShowBatchResults(true);
    setBatchUploading(false);
  };

  // Cancel batch results without sending email (X button)
  const cancelBatchResults = () => {
    setShowBatchResults(false);
    setBatchResults(null);
    setBatchMismatch(null);
    setCorrectedBatchTotal("");
    // Clear purpose now since user is canceling
    setPurpose("");
    setPurposeDetails("");
    toast.info("Batch upload canceled. No approval email sent.");
  };

  const closeBatchResults = async () => {
    // When user clicks Done, send the approval email with final amounts
    // But only if there were successful uploads
    if (batchResults && batchResults.batch_id && batchResults.processed_files > 0) {
      // Validate purpose before sending - it's mandatory
      const finalPurpose = purpose || batchResults.purpose;
      if (!finalPurpose || finalPurpose.trim() === "") {
        toast.error("Purpose is required. Please select a purpose before submitting.");
        return;
      }
      
      // Validate comments - mandatory
      if (!userComments || !userComments.trim()) {
        toast.error("Additional Comments is required. Please provide comments before submitting.");
        return;
      }
      
      try {
        toast.info("Sending approval email...");
        await axios.post(`${API}/invoices/batch/${batchResults.batch_id}/send-approval`, {
          uploader_name: uploaderName,
          company_name: companyName,
          purpose: finalPurpose,
          user_currency: currency,
          user_comments: userComments.trim()
        });
        toast.success("Approval email sent with your confirmed amounts!");
        setShowUploadSuccess(true); // Show success message
        // Auto-hide after 10 seconds
        setTimeout(() => setShowUploadSuccess(false), 10000);
      } catch (error) {
        console.error("Error sending approval email:", error);
        const errorMsg = error.response?.data?.detail || "Failed to send approval email. Please contact admin.";
        toast.error(errorMsg);
        return; // Don't close modal if email fails
      }
    }
    
    setShowBatchResults(false);
    setBatchResults(null);
    setBatchMismatch(null);
    setCorrectedBatchTotal("");
    // Clear purpose after successful email send
    setPurpose("");
    setPurposeDetails("");
  };

  const updateInvoiceAmount = async (invoiceId, newAmount, index) => {
    // Update single invoice amount
    try {
      await axios.put(`${API}/invoices/${invoiceId}`, {
        user_value: newAmount
      });
      
      // Update local state
      const updatedInvoices = [...batchResults.invoices];
      const currencySymbol = getCurrencySymbol(currency);
      updatedInvoices[index] = {
        ...updatedInvoices[index],
        extracted_amount: `${currencySymbol}${parseFloat(newAmount).toFixed(2)}`
      };
      
      // Recalculate total
      const newTotal = updatedInvoices
        .filter(inv => inv.status === 'completed')
        .reduce((sum, inv) => {
          const amt = parseFloat((inv.extracted_amount || "0").replace(/[^0-9.-]/g, '')) || 0;
          return sum + amt;
        }, 0);
      
      setBatchResults(prev => ({
        ...prev,
        total_amount: `${currencySymbol}${newTotal.toFixed(2)}`,
        invoices: updatedInvoices
      }));
      
      // Check if mismatch is resolved
      if (batchMismatch) {
        const userExpected = batchMismatch.userTotal;
        if (Math.abs(newTotal - userExpected) < 0.01) {
          setBatchMismatch(null);
          toast.success("Total now matches your expected amount!");
        } else {
          setBatchMismatch(prev => ({
            ...prev,
            extractedTotal: newTotal,
            difference: newTotal - userExpected
          }));
        }
      }
      
      toast.success("Amount updated");
    } catch (error) {
      console.error("Error updating invoice:", error);
      toast.error("Failed to update amount");
    }
  };

  const confirmBatchTotal = async (useExtracted) => {
    // User confirmed to proceed with current amounts (mismatch accepted)
    setBatchMismatch(null);
    toast.success("Batch upload confirmed!");
  };

  const getCurrencySymbol = (curr) => {
    const symbols = {
      GBP: "£", USD: "$", EUR: "€", INR: "₹", AED: "د.إ",
      JPY: "¥", CNY: "¥", KRW: "₩", SGD: "S$", HKD: "HK$",
      AUD: "A$", NZD: "NZ$", THB: "฿", MYR: "RM", IDR: "Rp",
      PHP: "₱", VND: "₫", SAR: "﷼", ILS: "₪", TRY: "₺",
      QAR: "﷼", CHF: "CHF", SEK: "kr", NOK: "kr", DKK: "kr",
      PLN: "zł", CZK: "Kč", HUF: "Ft", RON: "lei", CAD: "C$",
      MXN: "MX$", BRL: "R$", ARS: "AR$", CLP: "CL$", COP: "CO$",
      ZAR: "R", EGP: "E£", NGN: "₦", KES: "KSh", RUB: "₽"
    };
    return symbols[curr] || curr + " ";
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      if (uploadMode === "batch") {
        // Batch mode: accept multiple files
        const files = Array.from(e.dataTransfer.files);
        if (files.length > 20) {
          toast.error("Maximum 20 files allowed per batch upload");
          return;
        }
        
        // Validate file types
        const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp", "application/pdf"];
        const invalidFiles = files.filter(f => !allowedTypes.includes(f.type));
        if (invalidFiles.length > 0) {
          toast.error(`Invalid file type(s): ${invalidFiles.map(f => f.name).join(", ")}`);
          return;
        }
        
        // Add to existing batch files (up to 10 total)
        setBatchFiles(prev => {
          const combined = [...prev, ...files];
          if (combined.length > 20) {
            toast.warning(`Only first 20 files kept (you had ${prev.length}, dropped ${files.length})`);
            return combined.slice(0, 20);
          }
          return combined;
        });
      } else {
        // Single mode: take first file only
        handleFileSelect(e.dataTransfer.files[0]);
      }
    }
  };

  const openCamera = () => {
    if (!uploaderName.trim()) {
      toast.error("Please enter your name first");
      return;
    }
    if (!companyName) {
      toast.error("Please select a company first");
      return;
    }
    if (!value.trim()) {
      toast.error("Please enter the value first");
      return;
    }
    if (!currency) {
      toast.error("Please select a currency first");
      return;
    }
    if (!purpose.trim()) {
      toast.error("Please enter the purpose first");
      return;
    }
    if (!paymentType) {
      toast.error("Please select a payment type first");
      return;
    }
    if (!userEmail.trim()) {
      toast.error("Please enter your email first");
      return;
    }
    cameraInputRef.current?.click();
  };

  const openFilePicker = () => {
    if (!uploaderName.trim()) {
      toast.error("Please enter your name first");
      return;
    }
    if (!companyName) {
      toast.error("Please select a company first");
      return;
    }
    if (!value.trim()) {
      toast.error("Please enter the value first");
      return;
    }
    if (!currency) {
      toast.error("Please select a currency first");
      return;
    }
    if (!purpose.trim()) {
      toast.error("Please enter the purpose first");
      return;
    }
    if (!paymentType) {
      toast.error("Please select a payment type first");
      return;
    }
    if (!userEmail.trim()) {
      toast.error("Please enter your email first");
      return;
    }
    fileInputRef.current?.click();
  };

  const handleAdminLogin = () => {
    const email = adminEmail.trim().toLowerCase();
    if (ADMIN_EMAILS.includes(email)) {
      setIsAdmin(true);
      localStorage.setItem("adminEmail", email);
      fetchInvoices();
      setShowAdminLogin(false);
      toast.success("Admin access granted");
    } else {
      toast.error("Unauthorized email. Contact admin for access.");
    }
  };

  const handleAdminLogout = () => {
    setIsAdmin(false);
    setAdminEmail("");
    setInvoices([]);
    localStorage.removeItem("adminEmail");
    toast.info("Logged out of admin view");
  };

  const syncAllToSheets = async () => {
    try {
      toast.info("Syncing invoices to Google Sheets...");
      const response = await axios.post(`${API}/sheets/sync-all`);
      toast.success(response.data.message);
    } catch (error) {
      console.error("Error syncing to sheets:", error);
      toast.error(error.response?.data?.detail || "Failed to sync to Google Sheets");
    }
  };

  const formatAndFilterSheet = async () => {
    try {
      toast.info("Formatting sheet with filters and clean styling...");
      const response = await axios.post(`${API}/sheets/format-and-filter`);
      toast.success(response.data.message);
    } catch (error) {
      console.error("Error formatting sheet:", error);
      toast.error(error.response?.data?.detail || "Failed to format sheet");
    }
  };

  const migrateToGCS = async () => {
    try {
      toast.info("Running GCS migration check...");
      const response = await axios.post(`${API}/admin/migrate-to-gcs`);
      const data = response.data;
      if (data.summary.missing_files_count > 0) {
        toast.warning(`Migration complete. ${data.summary.missing_files_count} files are missing and need manual recovery.`);
      } else {
        toast.success(`Migration complete. ${data.summary.already_in_gcs + data.summary.migrated_to_gcs} files verified in GCS.`);
      }
    } catch (error) {
      console.error("Error running migration:", error);
      toast.error(error.response?.data?.detail || "Failed to run migration");
    }
  };

  const regenerateDownloadURLs = async () => {
    try {
      toast.info("Regenerating download URLs in Google Sheet...");
      const response = await axios.post(`${API}/sheets/regenerate-download-urls`);
      toast.success(`${response.data.rows_updated} download URLs regenerated`);
    } catch (error) {
      console.error("Error regenerating URLs:", error);
      toast.error(error.response?.data?.detail || "Failed to regenerate URLs");
    }
  };

  const refreshSummaryOnly = async () => {
    try {
      toast.info("Refreshing Summary sheet...");
      const response = await axios.post(`${API}/sheets/refresh-summary`);
      toast.success(response.data.message);
    } catch (error) {
      console.error("Error refreshing summary:", error);
      toast.error(error.response?.data?.detail || "Failed to refresh summary");
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      completed: "bg-[hsl(var(--success))] text-white",
      processing: "bg-[hsl(var(--warning))] text-white",
      pending: "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]",
      failed: "bg-[hsl(var(--destructive))] text-white",
    };

    const icons = {
      completed: <CheckCircle className="w-3 h-3" />,
      processing: <Loader2 className="w-3 h-3 animate-spin" />,
      pending: <Loader2 className="w-3 h-3" />,
      failed: <XCircle className="w-3 h-3" />,
    };

    return (
      <span
        data-testid={`status-badge-${status}`}
        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-sm text-xs font-medium uppercase tracking-wide ${styles[status]}`}
      >
        {icons[status]}
        {status}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-white" data-testid="invoice-dashboard">
      <Toaster position="top-right" richColors />
      
      {/* Connection Error Banner */}
      {loadingFailed && (
        <div className="bg-red-50 border-b-2 border-red-500 px-4 py-3 flex items-center justify-between" data-testid="connection-error-banner">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <span className="text-red-800 font-medium">Connection issue detected. Some data failed to load.</span>
          </div>
          <button
            onClick={retryAllLoads}
            className="px-4 py-1.5 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 transition-colors"
            data-testid="retry-connection-btn"
          >
            Retry Connection
          </button>
        </div>
      )}
      
      {/* Header */}
      <header className="border-b border-border bg-white sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <img 
                src="/chapters-logo-transparent.png" 
                alt="Chapters Logo" 
                className="h-12 sm:h-14 w-auto"
                data-testid="chapters-logo"
              />
              <h1 className="font-extrabold tracking-tight text-2xl sm:text-3xl lg:text-4xl" data-testid="page-title">
                Chapters' Employee Reimbursements
              </h1>
            </div>
            
            {/* Sheets Connection Status - Only show for admins */}
            {isAdmin && (
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
                <div className="flex items-center justify-center gap-2 px-4 py-2 border border-[hsl(var(--success))] bg-white rounded-sm" data-testid="sheets-connected-badge">
                  <CheckCircle className="w-4 h-4 text-[hsl(var(--success))]" />
                  <span className="text-sm font-medium">Auto-syncing to Sheets</span>
                </div>
                <Link
                  to="/file-recovery"
                  data-testid="file-recovery-link"
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-sm font-medium text-sm hover:-translate-y-0.5 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] transition-all"
                >
                  <AlertTriangle className="w-4 h-4" />
                  <span className="hidden sm:inline">File Recovery</span>
                  <span className="sm:hidden">Recovery</span>
                </Link>
                <button
                  onClick={syncAllToSheets}
                  data-testid="sync-all-button"
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-[hsl(var(--primary))] text-white rounded-sm font-medium text-sm hover:-translate-y-0.5 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] transition-all"
                >
                  <RefreshCw className="w-4 h-4" />
                  <span className="hidden sm:inline">Sync All to Sheets</span>
                  <span className="sm:hidden">Sync All</span>
                </button>
                <button
                  onClick={formatAndFilterSheet}
                  data-testid="format-sheet-button"
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-sm font-medium text-sm hover:-translate-y-0.5 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] transition-all"
                >
                  <FileText className="w-4 h-4" />
                  <span className="hidden sm:inline">Format Sheet</span>
                  <span className="sm:hidden">Format</span>
                </button>
                <button
                  onClick={regenerateDownloadURLs}
                  data-testid="regenerate-urls-button"
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-sm font-medium text-sm hover:-translate-y-0.5 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] transition-all"
                >
                  <RefreshCw className="w-4 h-4" />
                  <span className="hidden sm:inline">Fix URLs</span>
                  <span className="sm:hidden">URLs</span>
                </button>
                <button
                  onClick={refreshSummaryOnly}
                  data-testid="refresh-summary-button"
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-sm font-medium text-sm hover:-translate-y-0.5 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] transition-all"
                >
                  <FileText className="w-4 h-4" />
                  <span className="hidden sm:inline">Refresh Summary</span>
                  <span className="sm:hidden">Summary</span>
                </button>
                <button
                  onClick={handleAdminLogout}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 border border-border bg-white text-[hsl(var(--foreground))] rounded-sm font-medium text-sm hover:bg-[hsl(var(--secondary))] transition-all"
                >
                  Logout
                </button>
              </div>
            )}

            {/* Admin Login Button - Show for non-admins */}
            {!isAdmin && (
              <button
                onClick={() => setShowAdminLogin(true)}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 border border-[hsl(var(--primary))] text-[hsl(var(--primary))] rounded-sm font-medium text-sm hover:bg-[hsl(var(--primary))] hover:text-white transition-all"
              >
                <Lock className="w-4 h-4" />
                Admin Login
              </button>
            )}
          </div>
        </div>
        
        {/* Admin Login Modal */}
        {showAdminLogin && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-sm border-2 border-border p-6 sm:p-8 max-w-md w-full shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
              <h3 className="text-xl font-bold mb-4">Admin Access</h3>
              <p className="text-sm text-[hsl(var(--muted-foreground))] mb-4">
                Enter your authorized email to view all invoices
              </p>
              <input
                type="email"
                value={adminEmail}
                onChange={(e) => setAdminEmail(e.target.value)}
                placeholder="admin@company.com"
                className="w-full px-4 py-3 border border-border rounded-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))] mb-4"
                onKeyDown={(e) => e.key === 'Enter' && handleAdminLogin()}
              />
              <div className="flex gap-3">
                <button
                  onClick={handleAdminLogin}
                  className="flex-1 px-4 py-3 bg-[hsl(var(--primary))] text-white rounded-sm font-medium hover:-translate-y-0.5 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] transition-all"
                >
                  Login
                </button>
                <button
                  onClick={() => {
                    setShowAdminLogin(false);
                    setAdminEmail("");
                  }}
                  className="flex-1 px-4 py-3 border border-border bg-white rounded-sm font-medium hover:bg-[hsl(var(--secondary))] transition-all"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Clarification Notice Banner */}
        {clarifyInvoiceId && clarificationComments && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-sm" data-testid="clarification-notice">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-amber-800 mb-1">Clarification Requested</h3>
                <p className="text-sm text-amber-700 mb-2">The admin has requested additional information:</p>
                <div className="bg-white p-3 rounded border border-amber-100 text-sm text-gray-700 italic mb-4">
                  "{clarificationComments}"
                </div>
                
                <div className="bg-white p-4 rounded border border-amber-200">
                  <label className="block text-sm font-medium text-amber-800 mb-2">
                    Your Response / Clarification <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={clarificationResponse}
                    onChange={(e) => setClarificationResponse(e.target.value)}
                    placeholder="Enter your response to the clarification request..."
                    className="w-full p-3 border border-amber-200 rounded-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    rows={3}
                  />
                  
                  <div className="mt-4 flex gap-3">
                    <button
                      onClick={handleResubmitClarification}
                      disabled={!clarificationResponse.trim() || isResubmitting}
                      className="flex-1 px-4 py-3 bg-amber-600 text-white rounded-sm font-medium hover:bg-amber-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all"
                    >
                      {isResubmitting ? "Resubmitting..." : "Resubmit with Clarification"}
                    </button>
                    <button
                      onClick={() => {
                        setClarifyInvoiceId(null);
                        setClarificationComments("");
                        setClarificationResponse("");
                        toast.info("Clarification cancelled. You can upload a new invoice instead.");
                      }}
                      className="px-4 py-3 border border-amber-300 text-amber-700 rounded-sm font-medium hover:bg-amber-100 transition-all"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
                
                <p className="text-xs text-amber-600 mt-3">
                  Your original file and details are preserved. Just enter your response above and click "Resubmit".
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Upload Zone */}
        <div className="mb-12 sm:mb-16">
          {/* Upload Mode Toggle */}
          <div className="mb-6">
            {/* Instruction Message */}
            <p className="text-sm font-bold text-[hsl(var(--foreground))] mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-sm">
              If you have a single invoice to be submitted, please click on the Single File. In case of more than one file to be submitted, please click on the Multiple Files.
            </p>
            
            <div className="flex items-center gap-4 mb-4">
              <span className="text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))] font-medium">
                Upload Mode
              </span>
              <div className="flex bg-[hsl(var(--secondary))] rounded-sm p-1 border border-border">
                <button
                  onClick={() => setUploadMode("single")}
                  className={`px-4 py-2 text-sm font-medium rounded-sm transition-all ${
                    uploadMode === "single"
                      ? "bg-[hsl(var(--primary))] text-white shadow-sm"
                      : "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
                  }`}
                >
                  Single File
                </button>
                <button
                  onClick={() => setUploadMode("batch")}
                  className={`px-4 py-2 text-sm font-medium rounded-sm transition-all flex items-center gap-2 ${
                    uploadMode === "batch"
                      ? "bg-[hsl(var(--primary))] text-white shadow-sm"
                      : "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
                  }`}
                >
                  <FileStack className="w-4 h-4" />
                  Multiple Files
                </button>
              </div>
            </div>
            {uploadMode === "batch" && (
              <p className="text-sm text-[hsl(var(--muted-foreground))] bg-blue-50 border border-blue-100 rounded-sm p-3">
                📦 <strong>Batch Upload:</strong> Upload up to 20 invoices at once. Each file will be processed individually with shared details (employee, company, purpose).
              </p>
            )}
          </div>

          <div className="mb-6">
            <span className="text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))] font-medium mb-3 block">
              Your Information
            </span>
            <div className="max-w-full sm:max-w-md space-y-4">
              {/* Company Name - FIRST */}
              <div>
                <label className="block text-sm font-medium mb-2">Company Name <span className="text-red-500">*</span> <span className="text-xs text-gray-500">Mandatory</span></label>
                <select
                  value={companyName}
                  onChange={(e) => handleCompanyChange(e.target.value)}
                  data-testid="company-name-select"
                  className="w-full px-4 py-3 border border-border rounded-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))] transition-all text-base bg-white"
                >
                  <option value="">Select a company</option>
                  {companies.map((company) => (
                    <option key={company.id} value={company.name}>{company.name}</option>
                  ))}
                </select>
              </div>

              {/* Employee Name - Dropdown based on Company */}
              <div>
                <label className="block text-sm font-medium mb-2">Your Name <span className="text-red-500">*</span> <span className="text-xs text-gray-500">Mandatory</span></label>
                <select
                  value={uploaderName}
                  onChange={(e) => setUploaderName(e.target.value)}
                  disabled={!companyName}
                  data-testid="uploader-name-select"
                  className="w-full px-4 py-3 border border-border rounded-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))] transition-all text-base bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <option value="">{companyName ? "Select your name" : "Select a company first"}</option>
                  {getEmployeesForCompany().map((employee) => (
                    <option key={employee} value={employee}>{employee}</option>
                  ))}
                </select>
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium mb-2">Your Email <span className="text-red-500">*</span> <span className="text-xs text-gray-500">Mandatory</span></label>
                <input
                  type="email"
                  value={userEmail}
                  onChange={(e) => setUserEmail(e.target.value)}
                  placeholder="your.email@company.com"
                  data-testid="user-email-input"
                  className="w-full px-4 py-3 border border-border rounded-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))] transition-all text-base"
                />
                <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                  You will receive approval/rejection status on this email
                </p>
              </div>

              {/* Purpose - Dropdown */}
              <div>
                <label className="block text-sm font-medium mb-2">Purpose <span className="text-red-500">*</span> <span className="text-xs text-gray-500">Mandatory</span></label>
                <select
                  value={purpose}
                  onChange={(e) => handlePurposeChange(e.target.value)}
                  data-testid="purpose-select"
                  className="w-full px-4 py-3 border border-border rounded-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))] transition-all text-base bg-white"
                >
                  <option value="">Select purpose</option>
                  {purposes.map((p) => (
                    <option key={p.id} value={p.name}>{p.name}</option>
                  ))}
                </select>
              </div>

              {/* Description - Only show if purpose requires it (contains misc/other) */}
              {requiresDescription(purpose) && (
                <div>
                  <label className="block text-sm font-medium mb-2">Description <span className="text-red-500">*</span> <span className="text-xs text-gray-500">Mandatory</span></label>
                  <input
                    type="text"
                    value={purposeDetails}
                    onChange={(e) => setPurposeDetails(e.target.value)}
                    placeholder="Describe the expense purpose"
                    data-testid="purpose-details-input"
                    className="w-full px-4 py-3 border border-border rounded-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))] transition-all text-base"
                  />
                </div>
              )}

              {/* Value and Currency */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    {uploadMode === "batch" ? "Expected Total (optional)" : (<>Value <span className="text-red-500">*</span> <span className="text-xs text-gray-500">Mandatory</span></>)}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    placeholder={uploadMode === "batch" ? "e.g., 500.00 (for validation)" : "e.g., 150.50"}
                    data-testid="value-input"
                    className="w-full px-4 py-3 border border-border rounded-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))] transition-all text-base"
                  />
                  {uploadMode === "batch" && (
                    <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                      Enter your expected total to verify against extracted amounts
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Currency <span className="text-red-500">*</span> <span className="text-xs text-gray-500">Mandatory</span></label>
                  <select
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value)}
                    data-testid="currency-select"
                    className="w-full px-4 py-3 border border-border rounded-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))] transition-all text-base bg-white"
                  >
                    <option value="">Select</option>
                    {CURRENCY_OPTIONS.map((curr) => (
                      <option key={curr.value} value={curr.value}>{curr.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Payment Type <span className="text-red-500">*</span> <span className="text-xs text-gray-500">Mandatory</span></label>
                <select
                  value={paymentType}
                  onChange={(e) => setPaymentType(e.target.value)}
                  data-testid="payment-type-select"
                  className="w-full px-4 py-3 border border-border rounded-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))] transition-all text-base bg-white"
                >
                  <option value="">Select payment type</option>
                  {PAYMENT_TYPE_OPTIONS.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Additional Comments - Mandatory */}
            <div className="mt-4">
              <label className="block text-sm font-medium mb-2">Additional Comments <span className="text-red-500">*</span> <span className="text-xs text-gray-500">Mandatory</span></label>
              <textarea
                value={userComments}
                onChange={(e) => setUserComments(e.target.value)}
                placeholder="Add any additional notes or context for the approver (e.g., trip details, meeting attendees, project code)"
                data-testid="user-comments-input"
                rows={3}
                required
                className="w-full px-4 py-3 border border-border rounded-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))] transition-all text-base resize-none"
              />
              <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                Use this field to provide context or respond to clarification requests
              </p>
            </div>
          </div>
          
          <div className="mb-4">
            <span className="text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))] font-medium">
              Upload Zone
            </span>
          </div>

          {/* Single File Upload Mode */}
          {uploadMode === "single" && (
          <div
            data-testid="upload-zone"
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            className={`relative min-h-[250px] sm:min-h-[300px] border-2 border-dashed rounded-sm flex flex-col items-center justify-center p-8 sm:p-12 transition-all ${
              dragActive
                ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary)_/_0.05)]"
                : "border-border bg-white hover:border-[hsl(var(--primary))]"
            }`}
            style={{
              backgroundImage: dragActive
                ? "none"
                : "repeating-linear-gradient(0deg, transparent, transparent 49px, hsl(var(--border)) 49px, hsl(var(--border)) 50px), repeating-linear-gradient(90deg, transparent, transparent 49px, hsl(var(--border)) 49px, hsl(var(--border)) 50px)",
              backgroundSize: "50px 50px",
            }}
          >
            <div className="relative z-10 flex flex-col items-center gap-4 sm:gap-6 bg-white px-6 sm:px-8 py-6 w-full max-w-md">
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-sm border border-border flex items-center justify-center bg-[hsl(var(--secondary))]">
                {uploading || analyzing ? (
                  <Loader2 className="w-7 h-7 sm:w-8 sm:h-8 animate-spin text-[hsl(var(--primary))]" />
                ) : (
                  <Upload className="w-7 h-7 sm:w-8 sm:h-8 text-[hsl(var(--primary))]" />
                )}
              </div>
              
              <div className="text-center">
                <p className="text-base sm:text-lg font-semibold mb-2">
                  {analyzing ? "Analyzing document..." : uploading ? "Processing invoice..." : "Drop invoice here or choose below"}
                </p>
                <p className="text-xs sm:text-sm text-[hsl(var(--muted-foreground))]">
                  Supports: JPG, PNG, WEBP, PDF (multi-page supported)
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                {/* Camera Button - Shows on mobile */}
                <button
                  onClick={openCamera}
                  data-testid="camera-button"
                  disabled={uploading || analyzing || !uploaderName.trim()}
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-[hsl(var(--accent))] text-white rounded-sm font-medium hover:-translate-y-0.5 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                >
                  <Camera className="w-5 h-5" />
                  Scan Invoice
                </button>
                
                {/* File Picker Button */}
                <button
                  onClick={openFilePicker}
                  data-testid="file-upload-button"
                  disabled={uploading || analyzing || !uploaderName.trim()}
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-[hsl(var(--primary))] text-white rounded-sm font-medium hover:-translate-y-0.5 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                >
                  <FileText className="w-5 h-5" />
                  Choose File
                </button>
              </div>

              {/* Hidden file inputs */}
              <input
                ref={cameraInputRef}
                type="file"
                className="hidden"
                accept="image/*"
                capture="environment"
                onChange={(e) => handleFileSelect(e.target.files[0])}
                disabled={uploading || analyzing}
              />
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept="image/jpeg,image/jpg,image/png,image/webp,application/pdf"
                onChange={(e) => handleFileSelect(e.target.files[0])}
                disabled={uploading || analyzing}
              />
            </div>
          </div>
          )}

          {/* Batch Upload Mode */}
          {uploadMode === "batch" && (
            <div 
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-sm p-6 sm:p-8 bg-white transition-all ${
                dragActive
                  ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary)_/_0.05)]"
                  : "border-border hover:border-[hsl(var(--primary))]"
              }`}
            >
              <div className="flex flex-col items-center gap-4 mb-6">
                <div className={`w-14 h-14 sm:w-16 sm:h-16 rounded-sm border border-border flex items-center justify-center transition-all ${
                  dragActive ? "bg-[hsl(var(--primary)_/_0.1)] scale-110" : "bg-[hsl(var(--secondary))]"
                }`}>
                  {batchUploading ? (
                    <Loader2 className="w-7 h-7 sm:w-8 sm:h-8 animate-spin text-[hsl(var(--primary))]" />
                  ) : (
                    <FileStack className="w-7 h-7 sm:w-8 sm:h-8 text-[hsl(var(--primary))]" />
                  )}
                </div>
                <div className="text-center">
                  <p className="text-base sm:text-lg font-semibold mb-2">
                    {batchUploading 
                      ? `Processing ${batchFiles.length} files...` 
                      : dragActive 
                        ? "Drop files here!" 
                        : "Drag & drop or select multiple invoices"}
                  </p>
                  <p className="text-xs sm:text-sm text-[hsl(var(--muted-foreground))]">
                    Maximum 20 files (JPG, PNG, WEBP, PDF)
                  </p>
                </div>
                
                <button
                  onClick={() => batchFileInputRef.current?.click()}
                  disabled={batchUploading}
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-[hsl(var(--primary))] text-white rounded-sm font-medium hover:-translate-y-0.5 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FileStack className="w-5 h-5" />
                  Select Files
                </button>
                
                <input
                  ref={batchFileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  accept="image/jpeg,image/jpg,image/png,image/webp,application/pdf"
                  onChange={handleBatchFileSelect}
                  disabled={batchUploading}
                />
              </div>

              {/* Selected Files List */}
              {batchFiles.length > 0 && (
                <div className="border border-border rounded-sm overflow-hidden">
                  <div className="bg-[hsl(var(--secondary))] px-4 py-3 border-b border-border">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{batchFiles.length} file(s) selected</span>
                      <button
                        onClick={() => setBatchFiles([])}
                        className="text-sm text-red-600 hover:text-red-700"
                      >
                        Clear all
                      </button>
                    </div>
                  </div>
                  <div className="max-h-[200px] overflow-y-auto">
                    {batchFiles.map((file, index) => (
                      <div key={`${file.name}-${file.size}-${file.lastModified}`} className="flex items-center justify-between px-4 py-3 border-b border-border last:border-b-0 hover:bg-[hsl(var(--secondary)_/_0.5)]">
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-[hsl(var(--muted-foreground))]" />
                          <div>
                            <p className="text-sm font-medium truncate max-w-[200px] sm:max-w-[300px]">{file.name}</p>
                            <p className="text-xs text-[hsl(var(--muted-foreground))]">
                              {(file.size / 1024).toFixed(1)} KB
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => removeBatchFile(index)}
                          disabled={batchUploading}
                          className="text-red-500 hover:text-red-700 p-1"
                        >
                          <XCircle className="w-5 h-5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Upload Button */}
              {batchFiles.length > 0 && (
                <div className="mt-6 flex justify-center">
                  <button
                    onClick={handleBatchUpload}
                    disabled={batchUploading || !uploaderName.trim() || !companyName || !currency || !purpose || !paymentType || !userEmail}
                    className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-green-600 text-white rounded-sm font-medium text-lg hover:-translate-y-0.5 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                  >
                    {batchUploading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Upload className="w-5 h-5" />
                        Upload {batchFiles.length} Invoice{batchFiles.length > 1 ? 's' : ''}
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Batch Results Modal */}
        {showBatchResults && batchResults && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-sm border-2 border-border p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  {batchResults.extraction_complete === false ? (
                    <>
                      <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
                      <h3 className="text-xl font-bold">Extracting Invoice Data...</h3>
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-6 h-6 text-green-600" />
                      <h3 className="text-xl font-bold">Batch Upload Complete</h3>
                    </>
                  )}
                </div>
                <button onClick={cancelBatchResults} className="text-gray-500 hover:text-gray-700" title="Cancel (no email sent)">
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
              
              {/* Extraction in Progress Banner */}
              {batchResults.extraction_complete === false && (
                <div className="mb-4 p-4 bg-blue-50 border-2 border-blue-400 rounded-sm">
                  <div className="flex items-start gap-3">
                    <Loader2 className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5 animate-spin" />
                    <div className="flex-1">
                      <h4 className="font-bold text-blue-800 mb-2">AI Extraction in Progress</h4>
                      <p className="text-sm text-blue-700">
                        {batchResults.message || "Processing invoices..."}
                      </p>
                      <div className="mt-2 w-full bg-blue-200 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${Math.max(5, (batchResults.processed_files / Math.max(1, batchResults.total_files)) * 100)}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* All Files Failed - Duplicate Detection */}
              {batchResults.processed_files === 0 && batchResults.failed_files > 0 && (
                <div className="mb-4 p-4 bg-red-50 border-2 border-red-400 rounded-sm">
                  <div className="flex items-start gap-3">
                    <XCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="font-bold text-red-800 mb-2">All Invoices Already Submitted</h4>
                      <p className="text-sm text-red-700 mb-4">
                        These invoices have already been submitted previously and cannot be submitted again. 
                        Please check your previous submissions or upload different invoices.
                      </p>
                      <button
                        onClick={cancelBatchResults}
                        className="w-full px-4 py-3 bg-red-600 text-white rounded-sm font-medium hover:bg-red-700 transition-all flex items-center justify-center gap-2"
                      >
                        <XCircle className="w-5 h-5" />
                        Close and Start Over
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Mismatch Warning - Only show if some files were processed */}
              {batchMismatch && batchResults.processed_files > 0 && (
                <div className="mb-4 p-4 bg-amber-50 border-2 border-amber-400 rounded-sm">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="font-bold text-amber-800 mb-2">Total Amount Mismatch Detected!</h4>
                      <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                        <div className="bg-white p-3 rounded border border-amber-200">
                          <p className="text-[hsl(var(--muted-foreground))]">Your Expected Total</p>
                          <p className="text-xl font-bold">{getCurrencySymbol(batchMismatch.currency)}{batchMismatch.userTotal.toFixed(2)}</p>
                        </div>
                        <div className="bg-white p-3 rounded border border-amber-200">
                          <p className="text-[hsl(var(--muted-foreground))]">Extracted Total</p>
                          <p className="text-xl font-bold">{getCurrencySymbol(batchMismatch.currency)}{batchMismatch.extractedTotal.toFixed(2)}</p>
                        </div>
                      </div>
                      <div className={`text-sm font-medium mb-4 ${batchMismatch.difference > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        Difference: {batchMismatch.difference > 0 ? '+' : ''}{getCurrencySymbol(batchMismatch.currency)}{batchMismatch.difference.toFixed(2)}
                      </div>
                      
                      <p className="text-sm text-amber-700 mb-3">
                        Edit individual amounts below to correct, or proceed with extracted amounts.
                      </p>
                      
                      <button
                        onClick={() => confirmBatchTotal(true)}
                        className="w-full px-4 py-3 bg-amber-600 text-white rounded-sm font-medium hover:bg-amber-700 transition-all flex items-center justify-center gap-2"
                      >
                        <CheckCircle className="w-5 h-5" />
                        Proceed Anyway (Accept Mismatch)
                      </button>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Summary */}
              <div className="bg-[hsl(var(--secondary))] rounded-sm p-4 mb-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                  <div className="text-center">
                    <p className="text-[hsl(var(--muted-foreground))]">Total Files</p>
                    <p className="text-2xl font-bold">{batchResults.total_files}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-[hsl(var(--muted-foreground))]">Processed</p>
                    <p className="text-2xl font-bold text-green-600">{batchResults.processed_files}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-[hsl(var(--muted-foreground))]">Failed</p>
                    <p className="text-2xl font-bold text-red-600">{batchResults.failed_files}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-[hsl(var(--muted-foreground))]">Total Amount</p>
                    <p className={`text-2xl font-bold ${batchMismatch ? 'text-amber-600' : 'text-[hsl(var(--primary))]'}`}>{batchResults.total_amount}</p>
                  </div>
                </div>
              </div>

              {/* Invoice Breakdown with Editable Amounts */}
              <div className="border border-border rounded-sm overflow-hidden">
                <div className="bg-[hsl(var(--primary))] text-white px-4 py-3 flex justify-between items-center">
                  <span className="font-medium">Invoice Breakdown</span>
                  <span className="text-xs opacity-75">Click amount to edit</span>
                </div>
                <div className="max-h-[300px] overflow-y-auto">
                  {batchResults.invoices.map((inv, index) => (
                    <div key={inv.id || `${inv.filename}-${inv.upload_date}`} className={`flex items-center justify-between px-4 py-3 border-b border-border last:border-b-0 ${inv.status === 'failed' ? 'bg-red-50' : ''}`}>
                      <div className="flex items-center gap-3">
                        {inv.status === 'completed' ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-600" />
                        )}
                        <div>
                          <p className="text-sm font-medium truncate max-w-[200px]">{inv.filename}</p>
                          <p className="text-xs text-[hsl(var(--muted-foreground))]">
                            {inv.extracted_vendor || 'Vendor N/A'}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        {inv.status === 'completed' ? (
                          <EditableAmount
                            amount={inv.extracted_amount || '0'}
                            currency={currency}
                            onSave={(newAmount) => updateInvoiceAmount(inv.id, newAmount, index)}
                          />
                        ) : (
                          <p className="text-xs text-red-600">{inv.error_message}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Action Button - Only show if some files were processed */}
              {batchResults.processed_files > 0 && (
                <div className="mt-6 flex justify-end">
                  <button
                    onClick={closeBatchResults}
                    disabled={batchMismatch !== null || batchResults.extraction_complete === false}
                    data-testid="batch-done-button"
                    className="px-6 py-3 bg-[hsl(var(--primary))] text-white rounded-sm font-medium hover:-translate-y-0.5 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {batchResults.extraction_complete === false 
                      ? 'Extracting...' 
                      : batchMismatch 
                        ? 'Please resolve mismatch above' 
                        : 'Done'}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Multi-Page Invoice Review Modal */}
        {showMultiPageReview && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-sm border-2 border-border p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
              <div className="flex items-center gap-3 mb-4">
                <FileStack className="w-6 h-6 text-[hsl(var(--primary))]" />
                <h3 className="text-xl font-bold">Invoice Analysis Results</h3>
              </div>
              
              {analyzing ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader2 className="w-12 h-12 animate-spin text-[hsl(var(--primary))] mb-4" />
                  <p className="text-[hsl(var(--muted-foreground))]">Analyzing document pages...</p>
                </div>
              ) : multiPageResult ? (
                <>
                  {/* Summary */}
                  <div className="bg-[hsl(var(--secondary))] rounded-sm p-4 mb-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-[hsl(var(--muted-foreground))]">Pages Scanned:</span>
                        <span className="ml-2 font-medium">{multiPageResult.total_pages}</span>
                      </div>
                      <div>
                        <span className="text-[hsl(var(--muted-foreground))]">Invoices Found:</span>
                        <span className="ml-2 font-medium">{multiPageResult.invoices_detected}</span>
                      </div>
                    </div>
                  </div>

                  {/* Currency Mismatch Warning */}
                  {multiPageResult.has_currency_mismatch && (
                    <div className="bg-yellow-50 border border-yellow-300 rounded-sm p-4 mb-4">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium text-yellow-800">Multiple Currencies Detected</p>
                          <p className="text-sm text-yellow-700 mt-1">
                            Found: {multiPageResult.currencies_found.join(", ")}. Please confirm the correct currency for this claim.
                          </p>
                          <select
                            value={confirmedCurrency}
                            onChange={(e) => {
                              setConfirmedCurrency(e.target.value);
                              setCurrency(e.target.value);
                            }}
                            className="mt-2 px-3 py-2 border border-yellow-300 rounded-sm bg-white text-sm"
                          >
                            <option value="">Select currency</option>
                            {CURRENCY_OPTIONS.map((curr) => (
                              <option key={curr.value} value={curr.value}>{curr.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Detected Invoices List - Now shows all_invoices array */}
                  {multiPageResult.invoices_detected > 0 && (
                    <div className="mb-4">
                      <h4 className="text-sm font-medium mb-2 uppercase tracking-wide text-[hsl(var(--muted-foreground))]">
                        Detected Invoices ({multiPageResult.invoices_detected})
                      </h4>
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {(multiPageResult.all_invoices || []).map((invoice, idx) => (
                          <div key={invoice.id || `invoice-${idx}-${invoice.amount}`} className="border border-border rounded-sm p-3 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <span className="bg-[hsl(var(--accent))] text-white text-xs font-medium px-2 py-1 rounded-sm">
                                #{idx + 1}
                              </span>
                              <div>
                                <p className="font-medium">{invoice.vendor_name || "Unknown Vendor"}</p>
                                <p className="text-sm text-[hsl(var(--muted-foreground))]">{invoice.purpose || "No description"}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-lg">{invoice.amount || "N/A"}</p>
                              {invoice.currency_code && (
                                <p className="text-xs text-[hsl(var(--muted-foreground))]">{invoice.currency_code}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Total Summary */}
                  <div className="border-t-2 border-border pt-4 mb-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <DollarSign className="w-5 h-5 text-[hsl(var(--primary))]" />
                        <span className="font-medium">Total Claim Amount:</span>
                      </div>
                      <span className="text-2xl font-bold text-[hsl(var(--primary))]">
                        {multiPageResult.total_amount_formatted}
                      </span>
                    </div>
                    <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                      This amount has been pre-filled in the form. You can adjust it if needed.
                    </p>
                  </div>

                  {/* Editable Fields */}
                  <div className="space-y-3 mb-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Claim Amount</label>
                      <input
                        type="number"
                        step="0.01"
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        className="w-full px-3 py-2 border border-border rounded-sm"
                        placeholder="Enter amount"
                      />
                    </div>
                    {!multiPageResult.has_currency_mismatch && (
                      <div>
                        <label className="block text-sm font-medium mb-1">Currency</label>
                        <select
                          value={currency}
                          onChange={(e) => setCurrency(e.target.value)}
                          className="w-full px-3 py-2 border border-border rounded-sm"
                        >
                          <option value="">Select currency</option>
                          {CURRENCY_OPTIONS.map((curr) => (
                            <option key={curr.value} value={curr.value}>{curr.label}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-[hsl(var(--muted-foreground))]">
                  <p>No analysis data available. You can proceed with manual entry.</p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 mt-4">
                <button
                  onClick={cancelMultiPageReview}
                  className="flex-1 px-4 py-3 border border-border rounded-sm font-medium hover:bg-[hsl(var(--secondary))] transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={proceedWithUpload}
                  disabled={uploading || analyzing || (multiPageResult?.has_currency_mismatch && !confirmedCurrency)}
                  className="flex-1 px-4 py-3 bg-[hsl(var(--primary))] text-white rounded-sm font-medium hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      Proceed with Upload
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Invoices Table - Only visible to admins */}
        {isAdmin && (
          <div>
            {/* Employee Manager Toggle */}
            <div className="mb-6 border border-border rounded-sm p-4 bg-[hsl(var(--secondary)_/_0.3)]">
              <button
                onClick={() => {
                  setShowEmployeeManager(!showEmployeeManager);
                  if (!showEmployeeManager) fetchAllEmployees();
                }}
                className="flex items-center gap-2 text-sm font-medium text-[hsl(var(--primary))] hover:underline"
                data-testid="toggle-employee-manager"
              >
                <Users className="w-4 h-4" />
                {showEmployeeManager ? "Hide Employee Manager" : "Manage Employees"}
              </button>
              
              {showEmployeeManager && (
                <div className="mt-4 space-y-4">
                  {/* Add New Employee */}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newEmployeeName}
                      onChange={(e) => setNewEmployeeName(e.target.value)}
                      placeholder="Employee name"
                      className="flex-1 px-3 py-2 text-sm border border-border rounded-sm bg-background"
                      data-testid="new-employee-input"
                    />
                    <select
                      value={newEmployeeCompany}
                      onChange={(e) => setNewEmployeeCompany(e.target.value)}
                      className="px-3 py-2 text-sm border border-border rounded-sm bg-background"
                      data-testid="new-employee-company"
                    >
                      <option value="">Select Company</option>
                      {companies.map(company => (
                        <option key={company.id} value={company.name}>{company.name}</option>
                      ))}
                    </select>
                    <button
                      onClick={addEmployee}
                      className="px-4 py-2 text-sm font-medium bg-[hsl(var(--primary))] text-white rounded-sm hover:opacity-90"
                      data-testid="add-employee-btn"
                    >
                      Add
                    </button>
                  </div>
                  
                  {/* Employee List */}
                  <div className="max-h-64 overflow-y-auto border border-border rounded-sm">
                    <table className="w-full text-sm">
                      <thead className="bg-[hsl(var(--secondary))] sticky top-0">
                        <tr>
                          <th className="text-left px-3 py-2 font-medium">Name</th>
                          <th className="text-left px-3 py-2 font-medium">Company</th>
                          <th className="text-left px-3 py-2 font-medium">Status</th>
                          <th className="text-right px-3 py-2 font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allEmployees.map((emp) => (
                          <tr key={emp.id} className="border-t border-border">
                            <td className="px-3 py-2">
                              {editingEmployee?.id === emp.id ? (
                                <input
                                  type="text"
                                  value={editingEmployee.name}
                                  onChange={(e) => setEditingEmployee({...editingEmployee, name: e.target.value})}
                                  className="w-full px-2 py-1 text-sm border border-border rounded-sm"
                                />
                              ) : (
                                <span className={!emp.is_active ? "text-[hsl(var(--muted-foreground))] line-through" : ""}>
                                  {emp.name}
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2">
                              {editingEmployee?.id === emp.id ? (
                                <select
                                  value={editingEmployee.company}
                                  onChange={(e) => setEditingEmployee({...editingEmployee, company: e.target.value})}
                                  className="w-full px-2 py-1 text-sm border border-border rounded-sm"
                                >
                                  {companies.map(company => (
                                    <option key={company.id} value={company.name}>{company.name}</option>
                                  ))}
                                </select>
                              ) : (
                                <span className="text-xs text-[hsl(var(--muted-foreground))]">
                                  {emp.company}
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2">
                              <span className={`text-xs px-2 py-0.5 rounded-full ${emp.is_active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                                {emp.is_active ? "Active" : "Inactive"}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-right space-x-2">
                              {editingEmployee?.id === emp.id ? (
                                <>
                                  <button
                                    onClick={() => updateEmployee(emp.id, {name: editingEmployee.name, company: editingEmployee.company})}
                                    className="text-xs text-green-600 hover:underline"
                                  >
                                    Save
                                  </button>
                                  <button
                                    onClick={() => setEditingEmployee(null)}
                                    className="text-xs text-gray-500 hover:underline"
                                  >
                                    Cancel
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    onClick={() => setEditingEmployee({id: emp.id, name: emp.name, company: emp.company})}
                                    className="text-xs text-blue-600 hover:underline"
                                  >
                                    Edit
                                  </button>
                                  {emp.is_active ? (
                                    <button
                                      onClick={() => deleteEmployee(emp.id)}
                                      className="text-xs text-red-600 hover:underline"
                                    >
                                      Deactivate
                                    </button>
                                  ) : (
                                    <button
                                      onClick={() => restoreEmployee(emp.id)}
                                      className="text-xs text-green-600 hover:underline"
                                    >
                                      Restore
                                    </button>
                                  )}
                                </>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">
                    {allEmployees.filter(e => e.is_active).length} active employees across {companies.length} companies
                  </p>
                </div>
              )}
            </div>

            {/* Company Manager Toggle */}
            <div className="mb-6 border border-border rounded-sm p-4 bg-[hsl(var(--secondary)_/_0.3)]">
              <button
                onClick={() => {
                  setShowCompanyManager(!showCompanyManager);
                  if (!showCompanyManager) fetchAllCompanies();
                }}
                className="flex items-center gap-2 text-sm font-medium text-[hsl(var(--primary))] hover:underline"
                data-testid="toggle-company-manager"
              >
                <Building2 className="w-4 h-4" />
                {showCompanyManager ? "Hide Company Manager" : "Manage Companies"}
              </button>
              
              {showCompanyManager && (
                <div className="mt-4 space-y-4">
                  {/* Add New Company */}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newCompanyName}
                      onChange={(e) => setNewCompanyName(e.target.value)}
                      placeholder="New company name"
                      className="flex-1 px-3 py-2 text-sm border border-border rounded-sm bg-background"
                      data-testid="new-company-input"
                    />
                    <button
                      onClick={addCompany}
                      className="px-4 py-2 text-sm font-medium bg-[hsl(var(--primary))] text-white rounded-sm hover:opacity-90"
                      data-testid="add-company-btn"
                    >
                      Add
                    </button>
                  </div>
                  
                  {/* Company List */}
                  <div className="max-h-64 overflow-y-auto border border-border rounded-sm">
                    <table className="w-full text-sm">
                      <thead className="bg-[hsl(var(--secondary))] sticky top-0">
                        <tr>
                          <th className="text-left px-3 py-2 font-medium">Company Name</th>
                          <th className="text-left px-3 py-2 font-medium">Status</th>
                          <th className="text-right px-3 py-2 font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allCompanies.map((comp) => (
                          <tr key={comp.id} className="border-t border-border">
                            <td className="px-3 py-2">
                              {editingCompany?.id === comp.id ? (
                                <input
                                  type="text"
                                  value={editingCompany.name}
                                  onChange={(e) => setEditingCompany({...editingCompany, name: e.target.value})}
                                  className="w-full px-2 py-1 text-sm border border-border rounded-sm"
                                />
                              ) : (
                                <span className={!comp.is_active ? "text-[hsl(var(--muted-foreground))] line-through" : ""}>
                                  {comp.name}
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2">
                              <span className={`text-xs px-2 py-0.5 rounded-full ${comp.is_active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                                {comp.is_active ? "Active" : "Inactive"}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-right space-x-2">
                              {editingCompany?.id === comp.id ? (
                                <>
                                  <button
                                    onClick={() => updateCompany(comp.id, {name: editingCompany.name})}
                                    className="text-xs text-green-600 hover:underline"
                                  >
                                    Save
                                  </button>
                                  <button
                                    onClick={() => setEditingCompany(null)}
                                    className="text-xs text-gray-500 hover:underline"
                                  >
                                    Cancel
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    onClick={() => setEditingCompany({id: comp.id, name: comp.name})}
                                    className="text-xs text-blue-600 hover:underline"
                                  >
                                    Edit
                                  </button>
                                  {comp.is_active ? (
                                    <button
                                      onClick={() => deleteCompany(comp.id)}
                                      className="text-xs text-red-600 hover:underline"
                                    >
                                      Deactivate
                                    </button>
                                  ) : (
                                    <button
                                      onClick={() => restoreCompany(comp.id)}
                                      className="text-xs text-green-600 hover:underline"
                                    >
                                      Restore
                                    </button>
                                  )}
                                </>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">
                    {allCompanies.filter(c => c.is_active).length} active companies
                  </p>
                </div>
              )}
            </div>

            {/* Purpose Manager Toggle */}
            <div className="mb-6 border border-border rounded-sm p-4 bg-[hsl(var(--secondary)_/_0.3)]">
              <button
                onClick={() => {
                  setShowPurposeManager(!showPurposeManager);
                  if (!showPurposeManager) fetchAllPurposes();
                }}
                className="flex items-center gap-2 text-sm font-medium text-[hsl(var(--primary))] hover:underline"
                data-testid="toggle-purpose-manager"
              >
                <FileText className="w-4 h-4" />
                {showPurposeManager ? "Hide Purpose Manager" : "Manage Purposes"}
              </button>
              
              {showPurposeManager && (
                <div className="mt-4 space-y-4">
                  {/* Add New Purpose */}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newPurposeName}
                      onChange={(e) => setNewPurposeName(e.target.value)}
                      placeholder="New purpose name"
                      className="flex-1 px-3 py-2 text-sm border border-border rounded-sm bg-background"
                      data-testid="new-purpose-input"
                    />
                    <button
                      onClick={addPurpose}
                      className="px-4 py-2 text-sm font-medium bg-[hsl(var(--primary))] text-white rounded-sm hover:opacity-90"
                      data-testid="add-purpose-btn"
                    >
                      Add
                    </button>
                  </div>
                  
                  {/* Purpose List */}
                  <div className="max-h-64 overflow-y-auto border border-border rounded-sm">
                    <table className="w-full text-sm">
                      <thead className="bg-[hsl(var(--secondary))] sticky top-0">
                        <tr>
                          <th className="text-left px-3 py-2 font-medium">Purpose Name</th>
                          <th className="text-left px-3 py-2 font-medium">Status</th>
                          <th className="text-right px-3 py-2 font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allPurposes.map((purp) => (
                          <tr key={purp.id} className="border-t border-border">
                            <td className="px-3 py-2">
                              {editingPurpose?.id === purp.id ? (
                                <input
                                  type="text"
                                  value={editingPurpose.name}
                                  onChange={(e) => setEditingPurpose({...editingPurpose, name: e.target.value})}
                                  className="w-full px-2 py-1 text-sm border border-border rounded-sm"
                                />
                              ) : (
                                <span className={!purp.is_active ? "text-[hsl(var(--muted-foreground))] line-through" : ""}>
                                  {purp.name}
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2">
                              <span className={`text-xs px-2 py-0.5 rounded-full ${purp.is_active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                                {purp.is_active ? "Active" : "Inactive"}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-right space-x-2">
                              {editingPurpose?.id === purp.id ? (
                                <>
                                  <button
                                    onClick={() => updatePurpose(purp.id, {name: editingPurpose.name})}
                                    className="text-xs text-green-600 hover:underline"
                                  >
                                    Save
                                  </button>
                                  <button
                                    onClick={() => setEditingPurpose(null)}
                                    className="text-xs text-gray-500 hover:underline"
                                  >
                                    Cancel
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    onClick={() => setEditingPurpose({id: purp.id, name: purp.name})}
                                    className="text-xs text-blue-600 hover:underline"
                                  >
                                    Edit
                                  </button>
                                  {purp.is_active ? (
                                    <button
                                      onClick={() => deletePurpose(purp.id)}
                                      className="text-xs text-red-600 hover:underline"
                                    >
                                      Deactivate
                                    </button>
                                  ) : (
                                    <button
                                      onClick={() => restorePurpose(purp.id)}
                                      className="text-xs text-green-600 hover:underline"
                                    >
                                      Restore
                                    </button>
                                  )}
                                </>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">
                    {allPurposes.filter(p => p.is_active).length} active purposes
                  </p>
                </div>
              )}
            </div>

            <div className="mb-4 flex items-center justify-between">
              <span className="text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))] font-medium">
                Processed Invoices
              </span>
              <button
                onClick={fetchInvoices}
                data-testid="refresh-button"
                className="text-sm text-[hsl(var(--primary))] hover:underline font-medium"
              >
                Refresh
              </button>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-16" data-testid="loading-spinner">
                <Loader2 className="w-8 h-8 animate-spin text-[hsl(var(--primary))]" />
              </div>
            ) : invoices.length === 0 ? (
              <div className="border border-border rounded-sm p-8 sm:p-12 text-center" data-testid="empty-state">
                <FileText className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-4 text-[hsl(var(--muted-foreground))]" />
                <p className="text-base sm:text-lg font-medium text-[hsl(var(--muted-foreground))]">No invoices yet</p>
                <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">Uploaded invoices will appear here</p>
              </div>
            ) : (
              <div className="border border-border rounded-sm overflow-hidden overflow-x-auto" data-testid="invoices-table">
              <table className="w-full min-w-[640px]">
                <thead className="bg-[hsl(var(--secondary))] border-b border-border">
                  <tr>
                    <th className="text-left px-4 sm:px-6 py-3 sm:py-4 text-xs uppercase tracking-widest font-semibold">Date</th>
                    <th className="text-left px-4 sm:px-6 py-3 sm:py-4 text-xs uppercase tracking-widest font-semibold">Filename</th>
                    <th className="text-left px-4 sm:px-6 py-3 sm:py-4 text-xs uppercase tracking-widest font-semibold">Person</th>
                    <th className="text-left px-4 sm:px-6 py-3 sm:py-4 text-xs uppercase tracking-widest font-semibold">Amount</th>
                    <th className="text-left px-4 sm:px-6 py-3 sm:py-4 text-xs uppercase tracking-widest font-semibold hidden sm:table-cell">Purpose</th>
                    <th className="text-left px-4 sm:px-6 py-3 sm:py-4 text-xs uppercase tracking-widest font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((invoice, index) => (
                    <tr
                      key={invoice.id}
                      data-testid={`invoice-row-${index}`}
                      className="border-b border-border last:border-b-0 hover:bg-[hsl(var(--secondary)_/_0.5)] transition-colors"
                      style={{
                        animation: `fadeIn 0.3s ease-out ${index * 0.05}s both`,
                      }}
                    >
                      <td className="px-4 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm font-mono" data-testid={`invoice-date-${index}`}>
                        {new Date(invoice.upload_date).toLocaleDateString()}
                      </td>
                      <td className="px-4 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm" data-testid={`invoice-filename-${index}`}>
                        <div className="flex items-center gap-2">
                          <FileText className="w-3 h-3 sm:w-4 sm:h-4 text-[hsl(var(--muted-foreground))] flex-shrink-0" />
                          <span className="truncate max-w-[120px] sm:max-w-none">{invoice.filename}</span>
                        </div>
                      </td>
                      <td className="px-4 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm font-mono" data-testid={`invoice-person-${index}`}>
                        {invoice.uploader_name || "-"}
                      </td>
                      <td className="px-4 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm font-mono font-semibold" data-testid={`invoice-amount-${index}`}>
                        {invoice.extracted_data?.amount || "-"}
                      </td>
                      <td className="px-4 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm hidden sm:table-cell" data-testid={`invoice-purpose-${index}`}>
                        <span className="line-clamp-2">{invoice.extracted_data?.purpose || "-"}</span>
                      </td>
                      <td className="px-4 sm:px-6 py-3 sm:py-4" data-testid={`invoice-status-${index}`}>
                        {getStatusBadge(invoice.status)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )}
          </div>
        )}

        {/* Message for non-admin users - Only show after successful upload */}
        {!isAdmin && showUploadSuccess && (
          <div className="border border-border rounded-sm p-8 sm:p-12 text-center bg-[hsl(var(--secondary)_/_0.3)]">
            <CheckCircle className="w-12 h-12 mx-auto mb-4 text-[hsl(var(--success))]" />
            <p className="text-lg font-semibold mb-2">Upload Successful!</p>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              Your invoices are being processed and synced to the system.
              <br />
              Admins will review and approve them shortly.
            </p>
            <button
              onClick={() => setShowUploadSuccess(false)}
              className="mt-4 px-4 py-2 text-sm text-[hsl(var(--primary))] hover:underline"
            >
              Dismiss
            </button>
          </div>
        )}
      </main>

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

// File Recovery Page for Admin to re-upload missing invoice files
const FileRecovery = () => {
  const [missingFiles, setMissingFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    fetchMissingFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchMissingFiles = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/invoices/missing-files`);
      setMissingFiles(response.data.invoices || []);
    } catch (error) {
      console.error("Error fetching missing files:", error);
      toast.error("Failed to load missing files list");
    } finally {
      setLoading(false);
    }
  };

  const handleReupload = async (invoiceId, file) => {
    if (!file) return;

    setUploading(prev => ({ ...prev, [invoiceId]: true }));

    try {
      const formData = new FormData();
      formData.append("file", file);

      await axios.post(`${API}/invoices/${invoiceId}/reupload`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      toast.success("File uploaded successfully!");
      
      // Remove from the list
      setMissingFiles(prev => prev.filter(inv => inv.id !== invoiceId));
    } catch (error) {
      console.error("Error re-uploading file:", error);
      toast.error("Failed to upload file. Please try again.");
    } finally {
      setUploading(prev => ({ ...prev, [invoiceId]: false }));
    }
  };

  return (
    <div style={{
      minHeight: "100vh",
      background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
      padding: "20px"
    }}>
      <Toaster position="top-right" richColors />
      
      <div style={{
        maxWidth: "900px",
        margin: "0 auto"
      }}>
        {/* Header */}
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: "16px",
          marginBottom: "30px"
        }}>
          <button
            onClick={() => navigate("/")}
            style={{
              background: "rgba(255,255,255,0.1)",
              border: "none",
              borderRadius: "8px",
              padding: "10px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            <ArrowLeft size={24} color="white" />
          </button>
          <div>
            <h1 style={{
              color: "white",
              fontSize: "28px",
              fontWeight: "bold",
              margin: 0
            }}>
              File Recovery
            </h1>
            <p style={{
              color: "rgba(255,255,255,0.6)",
              margin: "4px 0 0 0",
              fontSize: "14px"
            }}>
              Re-upload missing invoice files
            </p>
          </div>
        </div>

        {/* Info Banner */}
        <div style={{
          background: "rgba(251, 191, 36, 0.1)",
          border: "1px solid rgba(251, 191, 36, 0.3)",
          borderRadius: "12px",
          padding: "16px 20px",
          marginBottom: "24px",
          display: "flex",
          gap: "12px",
          alignItems: "flex-start"
        }}>
          <AlertTriangle size={24} color="#fbbf24" style={{ flexShrink: 0, marginTop: "2px" }} />
          <div>
            <p style={{ color: "#fbbf24", fontWeight: "600", margin: "0 0 4px 0" }}>
              Invoice Files Need Recovery
            </p>
            <p style={{ color: "rgba(255,255,255,0.7)", margin: 0, fontSize: "14px" }}>
              The invoices below are missing their attached files. Please re-upload the original invoice file for each record. 
              Once uploaded, files will be permanently stored in cloud storage.
            </p>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div style={{
            textAlign: "center",
            padding: "60px 20px",
            color: "rgba(255,255,255,0.6)"
          }}>
            <Loader2 size={40} className="animate-spin" style={{ margin: "0 auto 16px" }} />
            <p>Loading invoices with missing files...</p>
          </div>
        )}

        {/* No Missing Files */}
        {!loading && missingFiles.length === 0 && (
          <div style={{
            textAlign: "center",
            padding: "60px 20px",
            background: "rgba(34, 197, 94, 0.1)",
            borderRadius: "12px",
            border: "1px solid rgba(34, 197, 94, 0.3)"
          }}>
            <CheckCircle size={48} color="#22c55e" style={{ margin: "0 auto 16px" }} />
            <h3 style={{ color: "#22c55e", margin: "0 0 8px 0" }}>All Files Present!</h3>
            <p style={{ color: "rgba(255,255,255,0.6)", margin: 0 }}>
              No invoices are missing their files. Everything is in order.
            </p>
          </div>
        )}

        {/* Missing Files List */}
        {!loading && missingFiles.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <p style={{ color: "rgba(255,255,255,0.6)", margin: "0 0 8px 0" }}>
              {missingFiles.length} invoice(s) need file recovery:
            </p>
            
            {missingFiles.map((invoice) => (
              <div
                key={invoice.id}
                style={{
                  background: "rgba(255,255,255,0.05)",
                  borderRadius: "12px",
                  padding: "20px",
                  border: "1px solid rgba(255,255,255,0.1)"
                }}
              >
                <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  flexWrap: "wrap",
                  gap: "16px"
                }}>
                  {/* Invoice Details */}
                  <div style={{ flex: 1, minWidth: "200px" }}>
                    <h3 style={{
                      color: "white",
                      fontSize: "18px",
                      fontWeight: "600",
                      margin: "0 0 8px 0"
                    }}>
                      {invoice.uploader_name || "Unknown"}
                    </h3>
                    <div style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "16px",
                      color: "rgba(255,255,255,0.6)",
                      fontSize: "14px"
                    }}>
                      <span>📄 {invoice.filename || "No filename"}</span>
                      <span>📅 {invoice.upload_date ? new Date(invoice.upload_date).toLocaleDateString() : "Unknown date"}</span>
                      <span>📝 {invoice.purpose || "No purpose"}</span>
                    </div>
                    <p style={{
                      color: "rgba(255,255,255,0.4)",
                      fontSize: "12px",
                      margin: "8px 0 0 0",
                      fontFamily: "monospace"
                    }}>
                      ID: {invoice.id}
                    </p>
                  </div>

                  {/* Upload Button */}
                  <div style={{ flexShrink: 0 }}>
                    <label
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "8px",
                        background: uploading[invoice.id] ? "rgba(59, 130, 246, 0.3)" : "#3b82f6",
                        color: "white",
                        padding: "12px 20px",
                        borderRadius: "8px",
                        cursor: uploading[invoice.id] ? "not-allowed" : "pointer",
                        fontWeight: "500",
                        transition: "all 0.2s"
                      }}
                    >
                      {uploading[invoice.id] ? (
                        <>
                          <Loader2 size={18} className="animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload size={18} />
                          Upload File
                        </>
                      )}
                      <input
                        type="file"
                        accept="image/*,application/pdf"
                        style={{ display: "none" }}
                        disabled={uploading[invoice.id]}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            handleReupload(invoice.id, file);
                          }
                          e.target.value = ""; // Reset input
                        }}
                      />
                    </label>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Refresh Button */}
        {!loading && (
          <div style={{ textAlign: "center", marginTop: "30px" }}>
            <button
              onClick={fetchMissingFiles}
              style={{
                background: "rgba(255,255,255,0.1)",
                border: "1px solid rgba(255,255,255,0.2)",
                color: "white",
                padding: "12px 24px",
                borderRadius: "8px",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                fontWeight: "500"
              }}
            >
              <RefreshCw size={18} />
              Refresh List
            </button>
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-spin {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/file-recovery" element={<FileRecovery />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;